import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { LeaderboardV2Store } from './leaderboard-v2.store';
import { ApiService } from '../../shared/services/core/api.service';
import { LeaderboardV2Entry } from './models/leaderboard-v2.model';
import { MonthlyLeaderboardEntry } from './models/monthly-leaderboard-v2.model';
import { LeaderboardType } from './models/leaderboard-v2-type.enum';

@Injectable({ providedIn: 'root' })
export class LeaderboardV2Service {
  private yearlyCache = new Map<string, Observable<LeaderboardV2Entry[]>>();
  private monthlyCache = new Map<string, Observable<MonthlyLeaderboardEntry>>();
  private monthDataCache = new Map<string, Observable<LeaderboardV2Entry[]>>();

  constructor(
    private leaderboardV2Store: LeaderboardV2Store,
    private api: ApiService
  ) {}

  fetchLeaderboardV2(amountToDisplay: number = 5, type: LeaderboardType) {
    const key = `${type}-${amountToDisplay}`;

    if (!this.yearlyCache.has(key)) {
      const request$ = this.api.get<LeaderboardV2Entry>(`leaderboard/v2/${type}?recordCount=${amountToDisplay}`).pipe(
        tap((entries) => {
          this.leaderboardV2Store.update(() => ({
            leaderboardV2: entries,
          }));
        }),
        shareReplay(1)
      );

      this.yearlyCache.set(key, request$);
    }

    return this.yearlyCache.get(key)!;
  }

  fetchMonthlyLeaderboardV2(amountToDisplay: number, type: LeaderboardType) {
    const key = `${type}-${amountToDisplay}`;

    if (!this.monthlyCache.has(key)) {
      const request$ = this.api
        .getAnySingle<MonthlyLeaderboardEntry>(`leaderboard/v2/${type}/monthly?recordCount=${amountToDisplay}`)
        .pipe(
          tap((entries) => {
            this.leaderboardV2Store.update(() => ({
              monthlyLeaderboardV2: entries,
            }));
          }),
          shareReplay(1)
        );

      this.monthlyCache.set(key, request$);
    }

    return this.monthlyCache.get(key)!;
  }

  fetchEachMonthLeaderboardV2(amountToDisplay: number, month: number, year: number, type: LeaderboardType) {
    const key = `${type}-${year}-${month}-${amountToDisplay}`;

    if (!this.monthDataCache.has(key)) {
      const request$ = this.api
        .get<LeaderboardV2Entry>(`leaderboard/v2/${type}/monthly/${month}/${year}?recordCount=${amountToDisplay}`)
        .pipe(shareReplay(1));

      this.monthDataCache.set(key, request$);
    }

    return this.monthDataCache.get(key)!;
  }
}
